// src/components/resumo/annotations/NotesSidebar.tsx
// 🎯 Barra lateral colapsável com filtros de categoria - Identidade GRAIA

import React, { useState } from 'react';
import { Note, NoteCategory } from './annotations.types';
import { Star, BookOpen, HelpCircle, Lightbulb, ChevronRight, StickyNote, Trash2 } from 'lucide-react';

interface NotesSidebarProps {
    notes: Note[];
    onNoteClick: (noteId: string) => void;
    onStartQuiz: () => void;
    onDeleteNote: (noteId: string) => void;
    darkMode?: boolean;
}

const NotesSidebar: React.FC<NotesSidebarProps> = ({
    notes,
    onNoteClick,
    onStartQuiz,
    onDeleteNote,
    darkMode = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<NoteCategory | 'all'>('all');

    const pageNotes = notes;

    const filteredNotes = selectedCategory === 'all'
        ? pageNotes
        : pageNotes.filter(n => n.category === selectedCategory);

    const categories: { value: NoteCategory | 'all'; label: string; icon: any; color: string }[] = [
        { value: 'all', label: 'Todas', icon: BookOpen, color: getComputedStyle(document.body).getPropertyValue('--platform-accent-light').trim() || '#2F7130' },
        { value: 'importante', label: 'Importante', icon: Star, color: '#fbbf24' },
        { value: 'revisar', label: 'Revisar', icon: BookOpen, color: '#3b82f6' },
        { value: 'duvida', label: 'Dúvida', icon: HelpCircle, color: '#ef4444' },
        { value: 'conceito', label: 'Conceito', icon: Lightbulb, color: '#10b981' }
    ];

    const getCategoryIcon = (category: NoteCategory) => {
        const cat = categories.find(c => c.value === category);
        return cat ? cat.icon : BookOpen;
    };

    const getCategoryColor = (category: NoteCategory) => {
        const cat = categories.find(c => c.value === category);
        return cat?.color || getComputedStyle(document.body).getPropertyValue('--platform-accent-light').trim();
    };

    const quizCount = pageNotes.filter(n => n.question).length;

    return (
        <>
            {/* ========== BOTÃO FLUTUANTE ========== */}
            {!isOpen && (
                <button
                    className="notes-floating-button"
                    onClick={() => setIsOpen(true)}
                    title="Abrir notas"
                >
                    <StickyNote size={24} />
                    {pageNotes.length > 0 && (
                        <span className="notes-floating-badge">{pageNotes.length}</span>
                    )}
                </button>
            )}

            {/* ========== SIDEBAR ========== */}
            <div className={`notes-sidebar ${isOpen ? 'open' : ''} ${darkMode ? 'dark' : ''}`}>
                {/* Header */}
                <div className="notes-sidebar-header">
                    <div className="notes-sidebar-title">
                        <StickyNote size={18} />
                        <h3>Notas</h3>
                        <span className="notes-count">{pageNotes.length}</span>
                    </div>
                    <button
                        className="notes-close-btn"
                        onClick={() => setIsOpen(false)}
                        title="Fechar notas"
                    >
                        <ChevronRight size={20} />
                    </button>
                </div>

                {/* Filtros */}
                <div className="notes-filters">
                    {categories.map(cat => {
                        const Icon = cat.icon;
                        const count = cat.value === 'all'
                            ? pageNotes.length
                            : pageNotes.filter(n => n.category === cat.value).length;

                        return (
                            <button
                                key={cat.value}
                                className={`notes-filter-btn ${selectedCategory === cat.value ? 'active' : ''}`}
                                onClick={() => setSelectedCategory(cat.value)}
                                style={{
                                    borderColor: selectedCategory === cat.value ? cat.color : 'transparent'
                                }}
                                title={cat.label}
                            >
                                <Icon size={14} />
                                <span>{count}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Lista de Notas */}
                <div className="notes-list">
                    {filteredNotes.length === 0 ? (
                        <div className="notes-empty">
                            <p>Nenhuma nota nesta categoria</p>
                        </div>
                    ) : (
                        filteredNotes.map(note => {
                            const Icon = getCategoryIcon(note.category);
                            const color = getCategoryColor(note.category);

                            return (
                                <div
                                    key={note.id}
                                    className="note-item"
                                    onClick={() => onNoteClick(note.id)}
                                    style={{ borderLeftColor: color }}
                                >
                                    <div className="note-item-header">
                                        <Icon size={12} style={{ color }} />
                                        <span className="note-item-category">
                                            {categories.find(c => c.value === note.category)?.label}
                                        </span>
                                        {note.question && (
                                            <span className="note-item-quiz-badge">Q</span>
                                        )}
                                        <button
                                            className="note-item-delete-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (window.confirm('Excluir esta nota?')) {
                                                    onDeleteNote(note.id);
                                                }
                                            }}
                                            title="Excluir nota"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                    <p className="note-item-text">
                                        {note.text.substring(0, 60)}
                                        {note.text.length > 60 ? '...' : ''}
                                    </p>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Botão Quiz */}
                {quizCount > 0 && (
                    <div className="notes-sidebar-footer">
                        <button
                            className="notes-quiz-btn"
                            onClick={onStartQuiz}
                        >
                            <BookOpen size={16} />
                            <span>Revisar Notas ({quizCount})</span>
                        </button>
                    </div>
                )}
            </div>

            {/* ========== OVERLAY ========== */}
            {isOpen && <div className="notes-overlay" onClick={() => setIsOpen(false)} />}
        </>
    );
};

export default NotesSidebar;